import { useMemo } from 'react';
import { PersonSelect, type PersonSelectOption } from '@wundo/ui';
import type { OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import { m } from '@/paraglide/messages.js';

export interface ManagerSelectProps {
  colleagues: readonly OneOnOneColleagueItem[];
  /** A colleague's id, "not listed", or null while nothing is chosen. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  disabled?: boolean;
  'aria-describedby'?: string;
}

/**
 * The colleagues of the Team page to pick a manager from, the one met most
 * often first. Shared by the one-time question and the last onboarding step.
 */
export function ManagerSelect({
  colleagues,
  value,
  onValueChange,
  disabled,
  'aria-describedby': ariaDescribedBy,
}: ManagerSelectProps) {
  const people = useMemo(() => managerOptions(colleagues), [colleagues]);

  return (
    <PersonSelect
      aria-label={m.manager_question_title()}
      aria-describedby={ariaDescribedBy}
      people={people}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      labels={{
        placeholder: m.manager_question_placeholder(),
        search: m.manager_question_search(),
        empty: (query) => m.manager_question_no_results({ query }),
        notListed: m.manager_question_not_listed(),
      }}
    />
  );
}

function managerOptions(
  colleagues: readonly OneOnOneColleagueItem[],
): PersonSelectOption[] {
  return [...colleagues]
    .sort(
      (left, right) =>
        right.one_on_one_count - left.one_on_one_count ||
        left.label.localeCompare(right.label),
    )
    .map((colleague) => ({
      id: colleague.id,
      name: colleague.label,
      seed: colleague.email,
      imageUrl: colleague.profile_image_url,
      description:
        colleague.one_on_one_count === 1
          ? m.manager_question_meta_one()
          : m.manager_question_meta({ count: colleague.one_on_one_count }),
    }));
}
